/**
 * SitemapPage — dense, professional, single-screen directory.
 *
 * Layout intentionally fits everything within ~one fold on desktop:
 *   - Tight one-row hero (eyebrow + h1 + lead inline)
 *   - 4-column directory grid (auto-collapses to 2 on tablet, 1 on mobile)
 *   - Each section: small eyebrow label + grouped link list with `▸` marker
 *   - Compact footer prompt
 *
 * Reached from the footer.
 */
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Mail } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";

const SECTIONS = [
  {
    titleEn: "Company",
    titleJa: "会社情報",
    accent: "#5B2E83",
    pages: [
      { path: "/", en: "Home", ja: "ホーム" },
      { path: "/about", en: "About Us", ja: "会社について" },
      { path: "/our-team", en: "Our AI Team", ja: "AIチーム" },
      { path: "/our-leaders", en: "Leadership Team", ja: "リーダーシップチーム" },
      { path: "/corporate-profile", en: "Corporate Profile", ja: "会社概要" },
      { path: "/careers", en: "Careers", ja: "採用情報" },
    ],
  },
  {
    titleEn: "Founder's Message",
    titleJa: "代表メッセージ",
    accent: "#0E5FB0",
    pages: [
      {
        path: "/founders-message/japanetccard",
        en: "JapanETCcard",
        ja: "JapanETCcard",
      },
      {
        path: "/founders-message/japantollreceipts",
        en: "JapanTollReceipts",
        ja: "JapanTollReceipts",
      },
    ],
  },
  {
    titleEn: "Services",
    titleJa: "サービス",
    accent: "#2F8F3C",
    pages: [
      { path: "/services", en: "All Services", ja: "サービス一覧" },
      { path: "/services/japanetccard", en: "JapanETCcard", ja: "JapanETCcard" },
      { path: "/services/japantollreceipts", en: "JapanTollReceipts", ja: "JapanTollReceipts" },
      { path: "/services/japanchangemoney", en: "JapanChangeMoney", ja: "JapanChangeMoney" },
    ],
  },
  {
    titleEn: "Updates & Voice",
    titleJa: "更新情報とお客様の声",
    accent: "#0F1D2D",
    pages: [
      { path: "/whats-new", en: "What's New", ja: "最新情報" },
      { path: "/roadmap", en: "Roadmap", ja: "ロードマップ" },
      { path: "/testimonials", en: "Testimonials", ja: "お客様の声" },
    ],
  },
  {
    titleEn: "Signboards",
    titleJa: "看板掲出",
    accent: "#2F8F3C",
    pages: [
      { path: "/signboards", en: "All Signboards", ja: "看板一覧" },
      { path: "/signboards/oyama-route-58", en: "Oyama — Route 58 (Okinawa)", ja: "大山 — 国道58号線 (沖縄)" },
      { path: "/signboards/b36a-camp-foster", en: "B-36A — Camp Foster (Okinawa)", ja: "B-36A — キャンプ・フォスター (沖縄)" },
      { path: "/signboards/b13-route-58", en: "B-13 — Route 58 (Okinawa)", ja: "B-13 — 国道58号線 (沖縄)" },
      { path: "/signboards/yokosuka-base", en: "Yokosuka — Headquarters", ja: "横須賀 — 本社拠点" },
    ],
  },
  {
    titleEn: "Support",
    titleJa: "サポート",
    accent: "#5B2E83",
    pages: [
      { path: "/contact", en: "Contact Us", ja: "お問い合わせ" },
      { path: "/sitemap", en: "Sitemap", ja: "サイトマップ" },
    ],
  },
  {
    titleEn: "Legal",
    titleJa: "法的情報",
    accent: "#64748B",
    pages: [
      { path: "/legal/terms", en: "Terms of Service", ja: "利用規約" },
      { path: "/legal/privacy", en: "Privacy Statement", ja: "プライバシーポリシー" },
      {
        path: "/legal/commercial-transactions",
        en: "Commercial Transactions",
        ja: "特定商取引法に基づく表記",
      },
    ],
  },
];

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

export default function SitemapPage() {
  const { lang = "en" } = useParams();
  const isJa = lang === "ja";

  return (
    <article className="bg-white" data-testid="sitemap-page">
      <SEO {...PAGE_SEO.sitemap[lang]} path={`/${lang}/sitemap`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "サイトマップ" : "Sitemap", url: `/${lang}/sitemap` },
        ])}
      />

      {/* ===== Compact hero ===== */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-8">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold text-[#5B2E83]">
              {isJa ? "サイトマップ" : "Sitemap"}
            </div>
            <h1
              data-testid="sitemap-page-h1"
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[30px] font-bold tracking-tight leading-[1.1]",
              ].join(" ")}
            >
              {isJa ? "サイトマップ" : "Every page on this site."}
            </h1>
            <span className="text-[#CBD5E1]">·</span>
            <p className="text-[13px] leading-[1.6] text-[#475569] max-w-2xl">
              {isJa
                ? "NoJo Enterprise の全公開ページを一覧で確認できます。"
                : "A complete index of every public page on NoJo Enterprise."}
            </p>
          </div>
          <div className="mt-3 inline-flex items-center gap-3 text-[11px] font-mono-display tracking-[0.16em] uppercase text-[#94A3B8]">
            <span>
              {SECTIONS.length} {isJa ? "セクション" : "sections"}
            </span>
            <span className="text-[#CBD5E1]">·</span>
            <span>
              {SECTIONS.reduce((n, s) => n + s.pages.length, 0)}{" "}
              {isJa ? "ページ" : "pages"}
            </span>
          </div>
        </div>
      </section>

      {/* ===== Dense directory grid =====
            Mobile: 1 col · Tablet (sm): 2 cols · Desktop (lg): 3 cols.
            We deliberately stay at 3 columns max so each link has room
            to breathe — going to 4 cols crammed labels in JA.        */}
      <section className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-12">
          <ul
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
            data-testid="sitemap-directory"
          >
            {SECTIONS.map((sec, i) => (
              <li
                key={sec.titleEn}
                data-testid={`sitemap-section-${i}`}
                className="rounded-2xl bg-white ring-1 ring-[#E2E8F0] p-5 md:p-6 transition-colors hover:ring-[#CBD5E1]"
              >
                {/* Eyebrow + heading — tight stack */}
                <div
                  className="flex items-center gap-2 text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold mb-2"
                  style={{ color: sec.accent }}
                >
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: sec.accent }}
                  />
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-[17px] md:text-[19px] font-bold tracking-tight leading-tight",
                  ].join(" ")}
                >
                  {pickL(sec.titleEn, sec.titleJa, lang)}
                </h2>

                {/* Links list — single column inside each card, tight rhythm */}
                <ul className="mt-3 space-y-1.5">
                  {sec.pages.map((p) => (
                    <li key={p.path}>
                      <Link
                        to={`/${lang}${p.path === "/" ? "" : p.path}`}
                        data-testid={`sitemap-link${p.path.replace(/\//g, "-") || "-home"}`}
                        className="group inline-flex items-baseline gap-1.5 text-[13.5px] leading-[1.5] text-[#1F2937] hover:text-[#0E5FB0] transition-colors"
                      >
                        <span
                          aria-hidden
                          className="text-[#CBD5E1] group-hover:text-[#0E5FB0] transition-colors"
                        >
                          ▸
                        </span>
                        <span className="underline-offset-2 group-hover:underline">
                          {pickL(p.en, p.ja, lang)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== Compact contact prompt ===== */}
      <section className="relative isolate bg-white">
        <PageSideAura testId="sitemap-cta-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-10">
          <div className="flex items-center justify-between gap-4 flex-wrap rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F8F9FA] px-5 md:px-6 py-4">
            <div className="flex items-center gap-3 min-w-0">
              <Mail className="h-4 w-4 shrink-0 text-[#5B2E83]" strokeWidth={2} />
              <p className="text-[13.5px] leading-[1.55] text-[#334155]">
                {isJa
                  ? "お探しのページが見つからない場合は、お問い合わせください。"
                  : "Can't find what you're looking for? We'll point you to the right page."}
              </p>
            </div>
            <Link
              to={`/${lang}/contact`}
              data-testid="sitemap-cta-contact"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-white text-[12.5px] font-semibold tracking-tight bg-[#0E5FB0] hover:bg-[#084785] transition-colors shrink-0"
            >
              {isJa ? "お問い合わせ" : "Contact Us"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
